import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';
import { fmtDateGlobal } from '../lib/dateFormat';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DesignPerformance = ({ user }) => {
  const [designs, setDesigns] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [brands, setBrands] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);

  // Detail dialog
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get('/brands').then(r => setBrands(r.data || [])).catch(() => {});
  }, []);

  const fetchDesigns = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterBrands.length) p.append('brand_id', filterBrands.join(','));
      if (search) p.append('search', search);
      const res = await api.get(`/design-performance-list?${p.toString()}`);
      setDesigns(res.data || []);
    } catch {
      toast.error('Failed to load designs');
    } finally {
      setLoading(false);
    }
  }, [filterBrands, search]);

  useEffect(() => { fetchDesigns(); }, [fetchDesigns]);

  const openDetail = async (designCode) => {
    setSelectedDesign(designCode);
    setDetailLoading(true);
    try {
      const res = await api.get(`/design-performance/${encodeURIComponent(designCode)}`);
      setDetail(res.data);
    } catch {
      toast.error('Failed to load design details');
    } finally {
      setDetailLoading(false);
    }
  };

  const brandOpts = brands.map(b => ({ value: b.id, label: b.name }));

  return (
    <div className="space-y-4 p-6" data-testid="design-performance-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="design-performance-title">
          Design Performance
        </h1>
        <p className="text-sm text-gray-500">Per-design lifecycle, sales trend, and top customers/cities</p>
      </div>

      {/* Filters */}
      <Card data-testid="design-perf-filters"><CardContent className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[180px]">
            <p className="text-xs text-gray-500 mb-1 font-medium">Brand</p>
            <MultiSelectFilter label="All Brands" options={brandOpts} selected={filterBrands} onChange={setFilterBrands} />
          </div>
          <div className="min-w-[200px]">
            <p className="text-xs text-gray-500 mb-1 font-medium">Search</p>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search design code..." className="h-8 text-xs" data-testid="design-search" />
          </div>
          {filterBrands.length > 0 && (
            <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={() => setFilterBrands([])}>Clear</Button>
          )}
          <span className="ml-auto text-xs text-gray-400">{designs.length} designs</span>
        </div>
      </CardContent></Card>

      {/* Designs Table */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-gray-400">Loading...</CardContent></Card>
      ) : designs.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400" data-testid="no-designs">No designs found</CardContent></Card>
      ) : (
        <Card data-testid="designs-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 w-14">Image</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Design</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Brand</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Sold</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Revenue</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Cost</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Profit</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Margin</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Stock</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500"></th>
                  </tr>
                </thead>
                <tbody>
                  {designs.map((d, i) => (
                    <tr key={d.design_code} className="border-b hover:bg-gray-50 transition-colors" data-testid={`design-row-${i}`}>
                      <td className="px-3 py-1.5">
                        {d.image ? (
                          <img src={`${API_BASE}${d.image}`} alt={d.design_code} className="w-10 h-10 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity" onClick={(e) => { e.stopPropagation(); setLightboxImg(`${API_BASE}${d.image}`); }} data-testid={`design-img-${i}`} />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-gray-400 text-[10px]">No img</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-bold text-gray-900">{d.design_code}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{d.brand}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-emerald-600">{d.total_sold}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">₹{(d.total_revenue || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-2.5 text-right text-gray-500">₹{(d.total_cost || 0).toLocaleString('en-IN')}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${(d.profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        ₹{(d.profit || 0).toLocaleString('en-IN')}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-medium ${(d.margin || 0) >= 20 ? 'text-emerald-600' : (d.margin || 0) >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                        {d.margin || 0}%
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-700">{d.total_stock}</td>
                      <td className="px-4 py-2.5 text-center">
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 text-xs h-7"
                          onClick={() => openDetail(d.design_code)} data-testid={`view-detail-${i}`}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selectedDesign} onOpenChange={v => { if (!v) { setSelectedDesign(null); setDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="design-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito' }}>
              {selectedDesign} — Performance
            </DialogTitle>
            <p className="text-sm text-gray-500">Complete lifecycle view for this design</p>
          </DialogHeader>

          {detailLoading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : detail?.error ? (
            <div className="p-8 text-center text-red-500">{detail.error}</div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Overview Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MiniCard label="Total Sold" value={detail.total_sold} sub={`Net: ${detail.net_sold}`} />
                <MiniCard label="Revenue" value={detail.total_revenue.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })} />
                <MiniCard label="Current Stock" value={detail.total_stock} sub={`Value: ${detail.stock_value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}`} />
                <MiniCard label="Returns" value={detail.total_returned} sub={`Purchased: ${detail.total_purchased}`} />
              </div>

              {/* Info Row */}
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span>Brand: <strong className="text-gray-700">{detail.brand}</strong></span>
                <span>Category: <strong className="text-gray-700">{detail.category}</strong></span>
                <span>WSP: <strong className="text-gray-700">{detail.avg_wsp}</strong></span>
                <span>Added: <strong className="text-gray-700">{fmtDateGlobal(detail.date_added)}</strong></span>
                <span>Age: <strong className="text-gray-700">{detail.days_since_added} days</strong></span>
              </div>

              {/* Size Stock */}
              {detail.size_stock?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5">Size-wise Stock</p>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.size_stock.map(s => (
                      <span key={s.size} className="text-xs bg-gray-100 border rounded px-2 py-1">
                        <span className="font-medium">{s.size}</span>: <span className={s.stock > 0 ? 'text-blue-600 font-bold' : 'text-gray-400'}>{s.stock}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Sales Trend */}
              {detail.sales_trend?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5">Monthly Sales Trend</p>
                  <div className="overflow-x-auto">
                    <div className="flex gap-1 items-end min-w-fit" style={{ height: 120 }}>
                      {detail.sales_trend.map(m => {
                        const maxQty = Math.max(...detail.sales_trend.map(t => t.qty), 1);
                        const h = Math.max((m.qty / maxQty) * 100, 4);
                        return (
                          <div key={m.month} className="flex flex-col items-center" style={{ minWidth: 40 }}>
                            <span className="text-[9px] text-gray-500 mb-0.5">{m.qty}</span>
                            <div className="w-6 bg-emerald-500 rounded-t" style={{ height: h }} title={`${m.month}: ${m.qty} pcs`} />
                            <span className="text-[8px] text-gray-400 mt-0.5">{m.month.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Top Customers */}
              {detail.top_customers?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5">Top Customers</p>
                  <div className="space-y-1">
                    {detail.top_customers.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
                          <span className="font-medium text-gray-800">{c.name}</span>
                          {c.city && <span className="text-gray-400">({c.city})</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-700">{c.qty} pcs</span>
                          <span className="text-gray-400">{c.amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Cities */}
              {detail.top_cities?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5">Top Cities</p>
                  <div className="flex flex-wrap gap-2">
                    {detail.top_cities.map((c, i) => (
                      <Badge key={i} className="bg-indigo-50 text-indigo-700 text-xs px-2 py-1">
                        {c.city}: {c.qty} pcs
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center cursor-pointer" onClick={() => setLightboxImg(null)} data-testid="design-lightbox">
          <img src={lightboxImg} alt="Design" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
};

const MiniCard = ({ label, value, sub }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <p className="text-[10px] text-gray-500 font-medium">{label}</p>
    <p className="text-lg font-bold text-gray-900 mt-0.5">{value}</p>
    {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
  </div>
);

export default DesignPerformance;
